<template>
  <app-dialog
    v-model="dialogForControls"
    :value.sync="dialogForControls"
    width="850"
    content-class="v-dialog__form">
    <v-card
      class="h-100"
      style="height: 28rem;">
      <v-card-title>
        <span>
          Invite to Project
        </span>
        <span
          class="sourcery__icon-wrapper black"
          @click="cancelCB">
          <v-icon
            color="white"
            size="15">
            mdi-close
          </v-icon>
        </span>
      </v-card-title>
      <v-card-text>
        <AppShareAutocomplete
          :users-initial="usersInitial"
          placeholder="Share with people"
          :highlight-member="(user) => !userIsInvitedToProject(user)"
          @remove="removeUser"
          @selected="userSelectedCB" />
        <div
          v-if="hasUninvitedUsers"
          class="mt-8 mx-auto d-flex flex-1 items-center align-center">
          <v-icon
            color="red"
            large
            class="mr-2">
            mdi-alert-circle
          </v-icon>
          <span class="fs-16 flex-grow-0">
            Users shown with red highlight are not yet invited to the project.
            Click to invite:
          </span>
          <div class="share-btn-wrapper ml-2">
            <app-icon-btn
              :icon-name="require('@/assets/icons/share-icon.svg')"
              :is-icon="false"
              icon-color="grey"
              :icon-height="30"
              :icon-width="36"
              text="Share Project"
              show-tooltip
              right
              @click="showShareModal" />
          </div>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          outlined
          @click="cancelCB">
          Cancel
        </v-btn>
        <v-btn
          class="white--text"
          color="blue"
          elevation="0"
          @click="saveCB">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </app-dialog>
</template>

<script>

/*********************************************
 * Note:
 * - A large portion of this component is taken from 'ProjectDetailsControlsView.
 *   Specifically, just the invite part.
 * - They don't want to open the view settings to invite users, but there's really no space
 *   and I don't have the UI/UX wizardry to make it work in the already cramped ProjectViewsManager.
 * - I just made this small section, which is instant compared to view settings.
 * - Did not bother refactoring the stuff here from ControlsView because it's unclear if
 *   this is final and it takes too much effort.
 ********************************************/

import {
  mapState,
  mapMutations,
  mapActions,
} from 'vuex';
import AppShareAutocomplete from '@/components/App/AppShareAutocomplete';
import ProjectViewsApi from '@/services/graphql/projectViews';
import UserProfileAPI from '@/services/graphql/userProfile';

export default {
  name: 'ProjectViewsManagerInvite',
  components: {
    AppShareAutocomplete,
  },
  props: {
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    queryInformation: {
      type: Object,
      default: () => {
      },
    },
    usersInitial: {
      type: Array,
      default: () => [],
    },
    usersToDelete: {
      type: Array,
      default: () => [],
    },
  },
  data: () => {
    return {
      users: [],
    };
  },
  computed: {
    ...mapState('ProjectDetailsTableSchedule', ['projectPermissions']),
    dialogForControls: {
      get() {
        return this.dialogStatus;
      },
      set(val) {
        this.$emit('update:dialogStatus', val);
      },
    },
    hasUninvitedUsers() {
      const initialUsers = this.usersInitial.filter((user) => !this.usersToDelete.includes(user.id));
      const concatUsers = this.users.concat(initialUsers);
      return concatUsers.some((user) => !this.userIsInvitedToProject(user));
    },
    uninvitedUsers() {
      const initialUsers = this.usersInitial.filter((user) => !this.usersToDelete.includes(user.id));
      const concatUsers = this.users.concat(initialUsers);
      return concatUsers.filter((user) => !this.userIsInvitedToProject(user));
    },
  },
  methods: {
    ...mapMutations([
      'spinner',
      'showShareModalAction',
      'setShareModalInitialUsers',
    ]),
    ...mapActions(['handleError']),

    userIsInvitedToProject(user) {
      const email = typeof user === 'object' ? user.email : user;
      return this.projectPermissions.some((perm) => perm.userInfo?.email === email);
    },
    userSelectedCB(selected) {
      this.users = selected;
    },
    removeUser(item) {
      this.$emit('userRemoved', item);
    },
    showShareModal() {
      this.setShareModalInitialUsers(this.uninvitedUsers() ?? []);
      this.showShareModalAction();
    },
    // We can get UserProfile (if we wait for autocomplete, OR just an email if uninvited/typed in).
    // - Try to get id for each first.
    async _resolvedUsers() {
      const usernames = [];
      const profilesToFetch = [];
      for (const item of this.users) {
        if (typeof item === 'string') {
          profilesToFetch.push(UserProfileAPI.getUserProfileByEmail({
            email: item,
          }));
        } else if (item.id) {
          usernames.push(item.id);
        }
      }
      const results = await Promise.allSettled(profilesToFetch);
      for (const result of results) {
        if (result.status === 'fulfilled') {
          const profiles = result.value?.data?.response;
          if (profiles.length) usernames.push(profiles[0].id);
        }
      }
      return usernames;
    },
    async grantPermissionForUsers(variables) {
      this.spinner(true);
      try {
        await ProjectViewsApi.grantPermissionForUsers(variables);
      } catch (err) {
        this.handleError(err);
      }
      this.spinner(false);
    },
    async deletePermissionForUsers(variables) {
      this.spinner(true);
      try {
        await ProjectViewsApi.deletePermissionForUsers(variables);
      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },

    async saveCB() {
      this.spinner(true);
      try {
        const usernames = await this._resolvedUsers(this.users);
        if (usernames.length) {
          await this.grantPermissionForUsers({
            ...this.queryInformation,
            usernames,
          });
        }
        if (this.usersToDelete.length) {
          await this.deletePermissionForUsers({
            ...this.queryInformation,
            usernames: this.usersToDelete,
          });
        }
        this.$emit('usersChanged');
        this.dialogForControls = false;

      } catch (err) {
        this.handleError(err);
      } finally {
        this.spinner(false);
      }
    },
    cancelCB() {
      this.dialogForControls = false;
    },
  },
};
</script>